import { createSuspenseQuery } from "../../utils/queryFactories.js";
import { api } from "../../utils/fetch.js";
import { z } from "zod";
import { BoardNotesSchema } from "../../schemas/note.js";

const EntityTypeSchema = z.enum(["task", "resource", "client"]);
export type EntityType = z.infer<typeof EntityTypeSchema>;

const BaseRequestSchema = z.object({
  id: z.int().positive(),
  api: z.enum(["wf", "public"]),
  entity_type: EntityTypeSchema
});

const RequestSchema = z.discriminatedUnion("api", [
  BaseRequestSchema.extend({
    api: z.literal("wf")
  }),
  BaseRequestSchema.extend({
    api: z.literal("public"),
    slug: z.string()
  })
]);

const RequestVariables = z.union([
  BaseRequestSchema.extend({ api: z.literal("wf") }).omit({ api: true }),
  BaseRequestSchema.extend({ api: z.literal("public"), slug: z.string() }).omit(
    { api: true }
  )
]);

const ResponseSchema = z.array(BoardNotesSchema);

type Response = z.infer<typeof ResponseSchema>;
type Request = z.infer<typeof RequestSchema>;

export type EntityNotesResponse = Response;
export type EntityNotesRequest = Request;

export type EntityNotesProps = Request;

const urls = {
  wf: {
    task: "/task/:id/notes",
    resource: "/resource/:id/notes",
    client: "/client/:id/notes"
  },
  public: {
    task: "/public/:slug/task/:id/notes",
    resource: "/public/:slug/resource/:id/notes",
    client: "/public/:slug/client/:id/notes"
  }
} as const;

export const useEntityNotesSuspense = (entity_type: EntityType) =>
  createSuspenseQuery<Response, Request, Error>({
    queryKey: [`entity${entity_type}NotesWithSuspense`],
    staleTime: 1000 * 60,
    fetcher: async (body) => {
      // console.log("body", body);
      const { api: apiType, ...rest } = body;
      const result = await api<Omit<Request, "api">, Response>({
        method: "GET",
        url: urls[apiType][rest.entity_type],
        requestSchema: RequestVariables,
        responseSchema: ResponseSchema,
        body: rest
      });
      return result;
    }
  });
